import { useParams } from "react-router-dom";
import React, { useEffect, useState } from "react";
import { Box, Card, Skeleton } from "@mui/material";
import {
  IDocument,
  IDocumentContentRequest,
  IDocumentsRequestParams,
} from "../../../../models/user/policyDetails/Document";
import { DocumentsStyles } from "./DocumentsStyles";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import { RootState } from "../../../../store/store";
import Document from "./Document/Document";
import {
  getBufferData,
  getDocTypeByTemplateIdOrName,
} from "../../../../utils/Utility";
import {
  GetInsuranceDocumentContent,
  GetInsuranceDocuments,
  VieworDownloadMockupDocumentsPA,
} from "../../../../store/user/policyDetails/policyDetailsActions";
import { setInsuranceDocumentContent } from "../../../../store/user/policyDetails/policyDetailsSlice";
import ActivityFilter from "../../../../common/ActivityFilter/ActivityFilter";
import { useTranslation } from "react-i18next";
import CustomSkeleton from "../../../../common/CustomSkeleton/CustomSkeleton";

const Documents: React.FC = () => {
  const dispatch = useAppDispatch();

  const { t } = useTranslation();

  const { policyId, carrier, system, lobCode } = useParams();

  const { insuranceDocuments, insuranceDocumentContent, policyTransactions } =
    useAppSelector((store: RootState) => store.policyDetails);
  const { isSuccess, calledReducerType, isError, isLoading } = useAppSelector(
    (store: RootState) => store.common
  );
  const [actionType, setActionType] = useState<string | null>(null);
  const [currentClickFile, setCurrentClickFile] = useState<IDocument | null>(
    null
  );
  const [selectedPolicyTerm, setSelectedPolicyTerm] = useState<string | null>(
    null
  );
  const handleFileClick = (type: string, documentData: IDocument) => {
    const docType = getDocTypeByTemplateIdOrName(
      documentData?.templateId,
      documentData?.name
    );
    if (!policyId || !carrier || !system || !lobCode || !docType) {
      return;
    }
    setActionType(type);
    setCurrentClickFile(documentData);
    const requestParams: IDocumentContentRequest = {
      policyId: policyId,
      lobCode,
      docType: docType,
      documentId: documentData?.documentId,
    };
    if (lobCode === "PA") {
      dispatch(VieworDownloadMockupDocumentsPA(requestParams));
    } else {
      dispatch(GetInsuranceDocumentContent(requestParams));
    }
  };

  useEffect(() => {
    if (
      isSuccess &&
      calledReducerType === "GetPolicyDocumentContent" &&
      insuranceDocumentContent &&
      actionType
    ) {
      const bufferArray = getBufferData(insuranceDocumentContent);
      const url = URL.createObjectURL(
        new Blob([bufferArray], { type: "application/pdf" })
      );
      if (actionType === "view") {
        window.open(url, "_blank");
      } else {
        const downloadLink = document.createElement("a");
        downloadLink.download = `${currentClickFile?.name}-${currentClickFile?.addDate}-${currentClickFile?.addTime}.pdf`;
        downloadLink.href = url;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
      }
      dispatch(setInsuranceDocumentContent(null));
    }
  }, [
    calledReducerType,
    isSuccess,
    insuranceDocumentContent,
    actionType,
    currentClickFile,
    dispatch,
  ]);

  useEffect(() => {
    if (
      isError &&
      calledReducerType === "GetPolicyDocumentContent" &&
      actionType
    ) {
      alert(`Error in ${actionType} document`);
    }
  }, [calledReducerType, isError, actionType]);

  useEffect(() => {
    if (policyId && carrier && system && lobCode) {
      const requestData: IDocumentsRequestParams[] = [
        {
          carrier,
          lobCode,
          policyId,
          policyTerm: "Current Term",
          system,
        },
      ];
      for (let [, policyTransaction] of Object.entries(policyTransactions)) {
        const ifExist = requestData.some(
          (policy: IDocumentsRequestParams) =>
            policy.policyId === policyTransaction.policyId
        );
        if (!ifExist && policyTransaction.policyId) {
          requestData.push({
            carrier,
            lobCode,
            policyId: policyTransaction.policyId,
            policyTerm: policyTransaction.policyTerm,
            system,
          });
        }
      }
      dispatch(GetInsuranceDocuments(requestData));
    }
  }, [policyId, dispatch, carrier, system, lobCode, policyTransactions]);

  const uniquePolicyTerms = Array.from(
    new Set(policyTransactions.map((transaction) => transaction.policyTerm))
  );

  const filteredTransactions = selectedPolicyTerm
    ? insuranceDocuments.filter(
        (document) => document.policyTerm === selectedPolicyTerm
      )
    : insuranceDocuments;

  return (
    <>
      <ActivityFilter
        items={uniquePolicyTerms}
        onFilterChange={setSelectedPolicyTerm}
        label="Filter by Term"
      />
      {!isLoading &&
        calledReducerType === "policyDetails/GetPolicyDocuments" &&
        (!filteredTransactions || filteredTransactions.length <= 0) && (
          <Card 
            sx={DocumentsStyles.card}
            id="documents_no_results"
            data-test-id="documents_no_results"
            aria-label="No documents found"
          >
            <Box 
              sx={DocumentsStyles.cardContentBox}
              id="documents_no_results_content"
              data-test-id="documents_no_results_content"
              aria-label="No documents found message"
            >
              {t("No documents found")}
            </Box>
          </Card>
        )}
      {isLoading && calledReducerType === "policyDetails/GetPolicyDocuments" ? (
        <CustomSkeleton height={100} count={5} direction="column"/>
      ) : (
        filteredTransactions &&
        filteredTransactions.length > 0 &&
        filteredTransactions?.map((document: IDocument, index: number) => (
          <Document
            key={`doc-list-${index}`}
            documentData={document}
            handleFileClick={handleFileClick}
            view={true}
          />
        ))
      )}
    </>
  );
};

export default Documents;
